import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetTokensPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/inputs/get-tokens-paginated.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { TokenCreateController } from '@app/feature/token/token-create/token-create.controller';
import { TokenListView } from '@app/feature/token/token-list/token-list.view';
import { useCpmsBoGetTokensPaginatedQuery } from '@service/cpms-bo-token.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { TokenListFilterSearchController } from '../token-list-filter-search/token-list-filter-search.controller';
import { TokenListFilterStateController } from '../token-list-filter-state/token-list-filter-state.controller';
import { TokenListFilterController } from '../token-list-filter/token-list-filter.controller';
import { TokenListOptionsController } from '../token-list-options/token-list-options.controller';
import {
  TokenListContextProvider,
  useTokenListContext,
} from '../token.context';
import {
  TOKEN_LIST_FILTER_NAME_LIST,
  TOKEN_LIST_SORT_FIELD,
} from './token-list.config';
import * as css from './token-list.module.scss';

export const TokenListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useTokenListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionCreateIsDialogOpen, setActionCreateIsDialogOpen] =
    useState(false);

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetTokensPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // TODO: Should be moved in filter-search component
  //   if (debouncedTextToSearch && debouncedTextToSearch.trim().length > 0) {
  //     params.textToSearch = debouncedTextToSearch.trim() || '';
  //   }

  // TODO: Should be move in dedicated component
  const filterNameActiveList = TOKEN_LIST_FILTER_NAME_LIST.filter(
    (filterName) => getPaginatedQueryParameters[filterName] !== undefined,
  );

  const filerActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.TOKENS,
        action: ActionsByResource.TOKENS.CREATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="create"
          icon="plus"
          onClick={() => setActionCreateIsDialogOpen(true)}
        />,
      );

      setPageAction(actions);
    }
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? TOKEN_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetTokensPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: TokenListViewProtected, status: resourceStatus } =
    withProtectedResource(TokenListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.TOKENS,
          action: ActionsByResource.TOKENS.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id: getPaginatedQueryParameters.sortField ?? TOKEN_LIST_SORT_FIELD,
        },
      ],
      columnVisibility: columnOptions.columnVisibility,
      columnOrder: columnOptions.columnOrder,
      renderColumnSettings: () => <TokenListOptionsController />,
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
      onHideColumn: handleHideColumn,
      onColumnOrderChange: handleColumnOrderChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <TokenListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <TokenListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filerActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filerActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>{TokenListViewProtected}</Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <>
          <Dialog
            open={actionCreateIsDialogOpen}
            onClose={() => setActionCreateIsDialogOpen(false)}
            confirmClose
            confirmCloseMessageValidate={t(
              'shared-design-system.dialog.confirm-close-message-validate',
            )}
            confirmCloseMessage={t(
              'shared-design-system.dialog.confirm-close-message',
            )}
            confirmCloseMessageCancel={t(
              'shared-design-system.dialog.confirm-close-message-cancel',
            )}
            height="100%"
            size={DialogSize.LARGE}>
            <Dialog.Header
              icon="credit-card-01"
              onClose={() => setActionCreateIsDialogOpen(false)}
              title={t('$.token-create.title')}
            />
            <Dialog.Content>
              <TokenCreateController
                onCancel={() => setActionCreateIsDialogOpen(false)}
                onSuccess={() => setActionCreateIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
          <Dialog
            onClose={() => setActionFilterIsDialogOpen(false)}
            open={actionFilterIsDialogOpen}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="filter-funnel-01"
              title={t('$.token-list-filter.title')}
              onClose={() => setActionFilterIsDialogOpen(false)}
            />
            <Dialog.Content>
              <TokenListFilterController
                onCancel={() => setActionFilterIsDialogOpen(false)}
                onSuccess={() => setActionFilterIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
        </>
      )}
    </Stack>
  );
};

export const TokenListControllerWithContext = (): React.JSX.Element => {
  return (
    <TokenListContextProvider>
      <TokenListController />
    </TokenListContextProvider>
  );
};
